import { signalUsageLimit, type UsageLimitSignal } from './paywall-signal';
type EventHandler = (data: any) => void;

export class WorkspaceWebSocket {
  private ws: WebSocket | null = null;
  private url: string;
  private apiKey: string;
  private handlers: Map<string, EventHandler[]> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private _connected = false;

  constructor(url: string, apiKey: string) {
    this.url = url;
    this.apiKey = apiKey;
  }

  connect(): void {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    // Attach the user's JWT so workspace-main can extract accountId during the
    // upgrade handshake. Without it, every WS conversation is saved with
    // account_id=NULL (historical bug pre-2026-04-17). localStorage key matches
    // what api.auth.login writes.
    let tokenParam = '';
    if (typeof window !== 'undefined') {
      const token = window.localStorage.getItem('ctrl-a-token')?.trim();
      if (token) tokenParam = `&token=${encodeURIComponent(token)}`;
    }
    const cleanKey = (this.apiKey || '').trim();
    const urlWithKey = `${this.url}?apiKey=${encodeURIComponent(cleanKey)}${tokenParam}`;
    this.ws = new WebSocket(urlWithKey);

    this.ws.onopen = () => {
      this._connected = true;
      // Order matters: capture whether this open is a reconnect BEFORE
      // resetting the attempts counter. The previous order reset
      // `reconnectAttempts` to 0 first, so `wasReconnect` was always false
      // and the `reconnected` event never fired (bug 2026-05-02). Listeners
      // (chat-context proactive re-bind, hook-level `setConnected(true)`)
      // depend on this event to re-announce `select-agent` after a drop.
      const wasReconnect = this.reconnectAttempts > 0;
      this.startPing();
      if (wasReconnect) {
        this.emit('reconnected', {});
      }
      this.reconnectAttempts = 0;
    };

    this.ws.onmessage = (event: MessageEvent) => {
      this.handleMessage(event);
    };

    this.ws.onclose = () => {
      this.handleClose();
    };

    this.ws.onerror = () => {
      // onerror is always followed by onclose, so let handleClose deal with it
    };
  }

  disconnect(): void {
    this.stopPing();
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this._connected = false;
    if (this.ws) {
      this.ws.onclose = null; // prevent reconnect on intentional disconnect
      this.ws.close();
      this.ws = null;
    }
  }

  selectAgent(agentId: string, conversationId?: string, accountId?: string): void {
    this.send({
      type: 'select-agent',
      agentId,
      ...(conversationId ? { conversationId } : {}),
      ...(accountId ? { accountId } : {}),
    });
  }

  sendMessage(message: string, conversationId?: string, accountId?: string, meta?: { pageContext?: string }): void {
    this.send({
      type: 'chat',
      content: message,
      ...(conversationId ? { conversationId } : {}),
      ...(accountId ? { accountId } : {}),
      ...(meta?.pageContext ? { pageContext: meta.pageContext } : {}),
    });
  }

  joinRoom(roomId: string): void {
    this.send({ type: 'join-room', roomId });
  }

  leaveRoom(roomId: string): void {
    this.send({ type: 'leave-room', roomId });
  }

  /** Abort the in-flight chat turn on the server. Idempotent — second
   *  cancel is a no-op. Server responds with turn-cancelled. */
  cancelTurn(): void {
    this.send({ type: 'cancel-turn' });
  }

  /** Hand off the in-flight chat turn to a background_tasks row. Server
   *  responds with turn-backgrounded (on success) or
   *  turn-backgrounded-failed (e.g. trial session, no active turn). */
  backgroundTurn(): void {
    this.send({ type: 'background-turn' });
  }

  on(event: string, handler: EventHandler): () => void {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, []);
    }
    this.handlers.get(event)!.push(handler);
    return () => this.off(event, handler);
  }

  off(event: string, handler: EventHandler): void {
    const list = this.handlers.get(event);
    if (!list) return;
    const idx = list.indexOf(handler);
    if (idx !== -1) list.splice(idx, 1);
  }

  get connected(): boolean {
    return this._connected;
  }

  private send(payload: object): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(payload));
    }
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const data = JSON.parse(event.data as string);
      const { type, ...rest } = data;
      // Free tier (2026-09-26): the hub attaches `usageLimit` when it refused a
      // turn because the trial ended or an allowance ran out. The refusal text
      // still renders as the agent's reply; this ALSO opens the humane paywall.
      if (rest && typeof rest === 'object' && 'usageLimit' in rest) {
        signalUsageLimit(
          (rest as { usageLimit?: UsageLimitSignal }).usageLimit,
          typeof (rest as { content?: unknown }).content === 'string' ? (rest as { content: string }).content : undefined,
        );
      }
      if (type) {
        this.emit(type, data);
      }
    } catch {
      // ignore malformed messages
    }
  }

  private handleClose(): void {
    this._connected = false;
    this.stopPing();
    this.emit('close', {});
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      return;
    }
    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    this.reconnectAttempts += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      this.send({ type: 'ping' });
    }, 25000);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private emit(event: string, data: any): void {
    const list = this.handlers.get(event);
    if (!list) return;
    for (const handler of list) {
      try {
        handler(data);
      } catch {
        // don't let one bad handler break the rest
      }
    }
  }
}
